/**
 * CORVANA PLATFORM STATISTICS - SINGLE SOURCE OF TRUTH
 * ======================================================
 * 
 * This file is the ONLY place where platform statistics should be defined.
 * All components across the app should import from here.
 * 
 * When you add new data sources, integrations, industries, or dashboards,
 * UPDATE THE COUNTS HERE and they will automatically update everywhere.
 */

// ============================================
// CORE PLATFORM STATISTICS
// ============================================

// Data Sources - Official government and industry sources
// Update this object when adding new data sources
export const DATA_SOURCES = {
  // Federal Government
  abs: { name: 'Australian Bureau of Statistics', category: 'Federal Government' },
  tra: { name: 'Tourism Research Australia', category: 'Federal Government' },
  jsa: { name: 'Jobs & Skills Australia', category: 'Federal Government' },
  austrac: { name: 'AUSTRAC', category: 'Federal Government' },
  
  // State Regulators - Liquor & Gaming
  lgnsw: { name: 'Liquor & Gaming NSW', category: 'State Regulator' },
  vcglr: { name: 'Victorian Commission for Gambling', category: 'State Regulator' },
  olgr: { name: 'Office of Liquor and Gaming Regulation (QLD)', category: 'State Regulator' },
  rwwa: { name: 'Racing and Wagering Western Australia', category: 'State Regulator' },
  cbs_sa: { name: 'Consumer and Business Services (SA)', category: 'State Regulator' },
  tlgc: { name: 'Tasmanian Liquor and Gaming Commission', category: 'State Regulator' },
  ntrc: { name: 'Northern Territory Racing Commission', category: 'State Regulator' },
  actgrc: { name: 'ACT Gambling and Racing Commission', category: 'State Regulator' },
  
  // Industry Regulators
  asic: { name: 'ASIC', category: 'Industry Regulator' },
  accc: { name: 'ACCC', category: 'Industry Regulator' },
  tga: { name: 'Therapeutic Goods Administration', category: 'Industry Regulator' },
  ahpra: { name: 'AHPRA', category: 'Industry Regulator' },
  asqa: { name: 'ASQA', category: 'Industry Regulator' },
  acecqa: { name: 'ACECQA', category: 'Industry Regulator' },
  acqsc: { name: 'Aged Care Quality and Safety Commission', category: 'Industry Regulator' },
  abcb: { name: 'Australian Building Codes Board', category: 'Industry Regulator' },
  
  // Industry Bodies
  aha: { name: 'Australian Hotels Association', category: 'Industry Body' },
  clubsnsw: { name: 'ClubsNSW', category: 'Industry Body' },
  lpa: { name: 'Live Performance Australia', category: 'Industry Body' },
  rca: { name: 'Restaurant & Catering Association', category: 'Industry Body' },
  ara: { name: 'Australian Retailers Association', category: 'Industry Body' },
  big: { name: 'Beauty Industry Group', category: 'Industry Body' },
  fitness_australia: { name: 'Fitness Australia / AUSactive', category: 'Industry Body' },
  mta: { name: 'Motor Traders Association', category: 'Industry Body' },
  reia: { name: 'Real Estate Institute of Australia', category: 'Industry Body' },
  pca: { name: 'Property Council of Australia', category: 'Industry Body' },
  lca: { name: 'Law Council of Australia', category: 'Industry Body' },
  cpa: { name: 'CPA Australia', category: 'Industry Body' },
  mba: { name: 'Master Builders Australia', category: 'Industry Body' },
  hia: { name: 'Housing Industry Association', category: 'Industry Body' },
  ai_group: { name: 'Australian Industry Group', category: 'Industry Body' },
  ahc: { name: 'Australian Health Coalition', category: 'Industry Body' },
  
  // Market Research Partners
  niq: { name: 'NielsenIQ', category: 'Market Research' },
  ibisworld: { name: 'IBISWorld', category: 'Market Research' },
  cotality: { name: 'Cotality (CoreLogic)', category: 'Market Research' },
  domain: { name: 'Domain', category: 'Market Research' },
  rea: { name: 'REA Group', category: 'Market Research' },
  bizbuysell: { name: 'BizBuySell', category: 'Market Research' },
  dealstream: { name: 'DealStream', category: 'Market Research' },
  
  // Government Departments
  doh: { name: 'Department of Health', category: 'Government Department' },
  doe: { name: 'Department of Education', category: 'Government Department' },
  doi: { name: 'Department of Industry', category: 'Government Department' },
  doemp: { name: 'Department of Employment', category: 'Government Department' },
  doinfra: { name: 'Department of Infrastructure', category: 'Government Department' },
  medicare: { name: 'Medicare', category: 'Government Department' },
  ato: { name: 'Australian Taxation Office', category: 'Government Department' },
  
  // Additional Data Sources (v5.0)
  safe_work: { name: 'Safe Work Australia', category: 'Government Department' },
  fair_work: { name: 'Fair Work Commission', category: 'Government Department' },
  apra: { name: 'APRA', category: 'Industry Regulator' },
  foodsafe: { name: 'Food Standards Australia', category: 'Industry Regulator' },
};

// Industries we fully support (31 industries - source of truth)
// This is THE single source of truth for all industry listings across the platform
export const SUPPORTED_INDUSTRIES = {
  // Core Business Industries (Original 15)
  crm: { name: 'CRM & Sales', icon: '🤝', color: 'blue' },
  hospitality: { name: 'Hospitality & Food Services', icon: '🍽️', color: 'orange', hasSubVerticals: true },
  retail: { name: 'Retail & E-commerce', icon: '🛍️', color: 'blue' },
  healthcare: { name: 'Healthcare & Medical', icon: '🏥', color: 'pink' },
  professional: { name: 'Professional Services', icon: '💼', color: 'indigo' },
  fitness: { name: 'Fitness & Wellness', icon: '💪', color: 'green' },
  property: { name: 'Property & Real Estate', icon: '🏢', color: 'amber' },
  construction: { name: 'Construction & Trades', icon: '🏗️', color: 'yellow' },
  automotive: { name: 'Automotive & Transport', icon: '🚗', color: 'red', hasSubVerticals: true },
  education: { name: 'Education & Training', icon: '🎓', color: 'purple' },
  beauty: { name: 'Beauty & Personal Care', icon: '💅', color: 'rose' },
  manufacturing: { name: 'Manufacturing & Production', icon: '🏭', color: 'slate' },
  entertainment: { name: 'Entertainment & Events', icon: '🎭', color: 'sky' },
  technology: { name: 'Technology & Software', icon: '💻', color: 'violet' },
  finance: { name: 'Finance & Banking', icon: '🏦', color: 'emerald' },
  
  // Industries Added v5.1 (7 more)
  agriculture: { name: 'Agriculture & Farming', icon: '🌾', color: 'lime' },
  logistics: { name: 'Logistics & Supply Chain', icon: '🚚', color: 'cyan' },
  trades: { name: 'Trades & Home Services', icon: '🔧', color: 'orange' },
  legal: { name: 'Legal Services', icon: '⚖️', color: 'slate' },
  accommodation: { name: 'Accommodation & Hotels', icon: '🏨', color: 'teal' },
  gaming: { name: 'Gaming & Esports', icon: '🎮', color: 'fuchsia' },
  environmental: { name: 'Environmental & Sustainability', icon: '🌿', color: 'green' },
  
  // Franchise & Service Industries Added v6.0 (9 more = 31 total)
  cleaning: { name: 'Cleaning Services', icon: '🧹', color: 'cyan', hasSubVerticals: true },
  pet_services: { name: 'Pet Services', icon: '🐕', color: 'amber', hasSubVerticals: true },
  childcare: { name: 'Childcare & Early Learning', icon: '👶', color: 'pink', hasSubVerticals: true },
  senior_care: { name: 'Senior Care & Home Care', icon: '🏠', color: 'rose' },
  pharmacy: { name: 'Pharmacy & Compounding', icon: '💊', color: 'blue' },
  staffing: { name: 'Staffing & Recruitment', icon: '👥', color: 'indigo' },
  printing: { name: 'Printing & Signage', icon: '🖨️', color: 'slate' },
  pest_control: { name: 'Pest Control', icon: '🐜', color: 'red' },
  moving: { name: 'Moving & Storage', icon: '📦', color: 'yellow' },
};

// Sub-verticals for industries with specialized segments (30+ sub-categories)
export const INDUSTRY_SUB_VERTICALS = {
  hospitality: [
    { id: 'qsr', name: 'Quick Service / Fast Food', icon: '🍔' },
    { id: 'full_service', name: 'Full Service Restaurant', icon: '🍽️' },
    { id: 'cafe_coffee', name: 'Café & Coffee', icon: '☕' },
    { id: 'bars_nightclubs', name: 'Bars & Nightclubs', icon: '🍸' },
    { id: 'catering', name: 'Catering Services', icon: '🥗' },
    { id: 'food_trucks', name: 'Food Trucks & Mobile', icon: '🚚' },
  ],
  automotive: [
    { id: 'dealership', name: 'New & Used Dealerships', icon: '🚗' },
    { id: 'service_repair', name: 'Service & Repair', icon: '🔧' },
    { id: 'car_wash', name: 'Car Wash', icon: '🚿' },
    { id: 'auto_detailing', name: 'Auto Detailing', icon: '✨' },
    { id: 'parts_accessories', name: 'Parts & Accessories', icon: '⚙️' },
    { id: 'tyres_wheels', name: 'Tyres & Wheels', icon: '🛞' },
  ],
  cleaning: [
    { id: 'commercial', name: 'Commercial Cleaning', icon: '🏢' },
    { id: 'residential', name: 'Residential Cleaning', icon: '🏠' },
    { id: 'carpet', name: 'Carpet & Floor', icon: '🧽' },
    { id: 'window', name: 'Window Cleaning', icon: '🪟' },
    { id: 'specialty', name: 'Specialty Cleaning', icon: '🔬' },
  ],
  pet_services: [
    { id: 'grooming', name: 'Pet Grooming', icon: '✂️' },
    { id: 'boarding', name: 'Pet Boarding', icon: '🏨' },
    { id: 'daycare', name: 'Pet Daycare', icon: '🐾' },
    { id: 'veterinary', name: 'Veterinary Clinics', icon: '🩺' },
    { id: 'retail', name: 'Pet Retail', icon: '🦴' },
  ],
  childcare: [
    { id: 'daycare', name: 'Daycare Centers', icon: '🏫' },
    { id: 'preschool', name: 'Preschool', icon: '🎨' },
    { id: 'after_school', name: 'After School Programs', icon: '📚' },
    { id: 'tutoring', name: 'Tutoring Services', icon: '✏️' },
    { id: 'early_learning', name: 'Early Learning', icon: '🧒' },
  ],
  retail: [
    { id: 'fashion', name: 'Fashion & Apparel', icon: '👕' },
    { id: 'electronics', name: 'Electronics', icon: '📱' },
    { id: 'grocery', name: 'Grocery & Food', icon: '🛒' },
    { id: 'home_garden', name: 'Home & Garden', icon: '🌱' },
    { id: 'specialty', name: 'Specialty Retail', icon: '🎁' },
  ],
};

// Role-based dashboards (v4.0 - 10 core roles)
export const ROLE_DASHBOARDS = {
  owner: { name: 'Owner/Director', access: 'Full access' },
  gm: { name: 'General Manager', access: 'Operations + Analytics' },
  kitchen_manager: { name: 'Kitchen Manager', access: 'Department specific' },
  bar_manager: { name: 'Bar Manager', access: 'Department specific' },
  foh_manager: { name: 'FOH Manager', access: 'Department specific' },
  accountant: { name: 'Accountant', access: 'Financial modules' },
  shift_supervisor: { name: 'Shift Supervisor', access: 'Shift operations' },
  staff: { name: 'Staff Member', access: 'Limited' },
  referral_partner: { name: 'Referral Partner', access: 'Partner portal' },
  advisor: { name: 'M&A Advisor', access: 'Valuation module' },
};

// Industry-specific dashboards (v5.1 - 44 industry views for all 22 industries)
export const INDUSTRY_DASHBOARDS = {
  // Hospitality
  hospitality_owner: { name: 'Hospitality Owner Dashboard', industry: 'hospitality' },
  hospitality_manager: { name: 'Hospitality Manager Dashboard', industry: 'hospitality' },
  // Retail
  retail_owner: { name: 'Retail Owner Dashboard', industry: 'retail' },
  retail_manager: { name: 'Retail Manager Dashboard', industry: 'retail' },
  // Healthcare
  healthcare_owner: { name: 'Healthcare Owner Dashboard', industry: 'healthcare' },
  healthcare_practitioner: { name: 'Healthcare Practitioner View', industry: 'healthcare' },
  // Professional Services
  professional_owner: { name: 'Professional Services Dashboard', industry: 'professional' },
  professional_partner: { name: 'Partner/Associate Dashboard', industry: 'professional' },
  // Construction
  construction_owner: { name: 'Construction Owner Dashboard', industry: 'construction' },
  construction_project: { name: 'Project Manager Dashboard', industry: 'construction' },
  // Technology
  technology_owner: { name: 'Tech Company Dashboard', industry: 'technology' },
  technology_product: { name: 'Product Dashboard', industry: 'technology' },
  // Manufacturing
  manufacturing_owner: { name: 'Manufacturing Owner Dashboard', industry: 'manufacturing' },
  manufacturing_production: { name: 'Production Dashboard', industry: 'manufacturing' },
  // Fitness
  fitness_owner: { name: 'Fitness Owner Dashboard', industry: 'fitness' },
  fitness_trainer: { name: 'Trainer Dashboard', industry: 'fitness' },
  // Beauty
  beauty_owner: { name: 'Beauty Business Dashboard', industry: 'beauty' },
  beauty_stylist: { name: 'Stylist Dashboard', industry: 'beauty' },
  // Automotive
  automotive_owner: { name: 'Automotive Owner Dashboard', industry: 'automotive' },
  automotive_service: { name: 'Service Manager Dashboard', industry: 'automotive' },
  // Finance
  finance_owner: { name: 'Finance Business Dashboard', industry: 'finance' },
  finance_advisor: { name: 'Advisor Dashboard', industry: 'finance' },
  // Education
  education_owner: { name: 'Education Owner Dashboard', industry: 'education' },
  education_instructor: { name: 'Instructor Dashboard', industry: 'education' },
  // Property & Real Estate
  property_owner: { name: 'Property Agency Dashboard', industry: 'property' },
  property_agent: { name: 'Agent Dashboard', industry: 'property' },
  // Entertainment
  entertainment_owner: { name: 'Entertainment Owner Dashboard', industry: 'entertainment' },
  entertainment_event: { name: 'Event Manager Dashboard', industry: 'entertainment' },
  // CRM & Sales
  crm_owner: { name: 'CRM Owner Dashboard', industry: 'crm' },
  crm_sales: { name: 'Sales Team Dashboard', industry: 'crm' },
  // Agriculture (NEW)
  agriculture_owner: { name: 'Agriculture Owner Dashboard', industry: 'agriculture' },
  agriculture_manager: { name: 'Farm Manager Dashboard', industry: 'agriculture' },
  // Logistics (NEW)
  logistics_owner: { name: 'Logistics Owner Dashboard', industry: 'logistics' },
  logistics_operations: { name: 'Operations Manager Dashboard', industry: 'logistics' },
  // Trades (NEW)
  trades_owner: { name: 'Trades Business Dashboard', industry: 'trades' },
  trades_tradesperson: { name: 'Tradesperson Dashboard', industry: 'trades' },
  // Legal (NEW)
  legal_owner: { name: 'Legal Practice Dashboard', industry: 'legal' },
  legal_partner: { name: 'Partner Dashboard', industry: 'legal' },
  // Accommodation (NEW)
  accommodation_owner: { name: 'Accommodation Owner Dashboard', industry: 'accommodation' },
  accommodation_manager: { name: 'Property Manager Dashboard', industry: 'accommodation' },
  // Gaming (NEW)
  gaming_owner: { name: 'Gaming Venue Dashboard', industry: 'gaming' },
  gaming_manager: { name: 'Venue Manager Dashboard', industry: 'gaming' },
  // Environmental (NEW)
  environmental_owner: { name: 'Environmental Business Dashboard', industry: 'environmental' },
  environmental_consultant: { name: 'Consultant Dashboard', industry: 'environmental' },
  // Cleaning Services (NEW v6.0)
  cleaning_owner: { name: 'Cleaning Business Dashboard', industry: 'cleaning' },
  cleaning_manager: { name: 'Operations Manager Dashboard', industry: 'cleaning' },
  // Pet Services (NEW v6.0)
  pet_services_owner: { name: 'Pet Business Dashboard', industry: 'pet_services' },
  pet_services_manager: { name: 'Location Manager Dashboard', industry: 'pet_services' },
  // Childcare (NEW v6.0)
  childcare_owner: { name: 'Childcare Center Dashboard', industry: 'childcare' },
  childcare_director: { name: 'Director Dashboard', industry: 'childcare' },
  // Senior Care (NEW v6.0)
  senior_care_owner: { name: 'Senior Care Dashboard', industry: 'senior_care' },
  senior_care_coordinator: { name: 'Care Coordinator Dashboard', industry: 'senior_care' },
  // Pharmacy (NEW v6.0)
  pharmacy_owner: { name: 'Pharmacy Owner Dashboard', industry: 'pharmacy' },
  pharmacy_manager: { name: 'Pharmacy Manager Dashboard', industry: 'pharmacy' },
  // Staffing (NEW v6.0)
  staffing_owner: { name: 'Staffing Agency Dashboard', industry: 'staffing' },
  staffing_recruiter: { name: 'Recruiter Dashboard', industry: 'staffing' },
  // Printing (NEW v6.0)
  printing_owner: { name: 'Print Shop Dashboard', industry: 'printing' },
  printing_production: { name: 'Production Manager Dashboard', industry: 'printing' },
  // Pest Control (NEW v6.0)
  pest_control_owner: { name: 'Pest Control Dashboard', industry: 'pest_control' },
  pest_control_technician: { name: 'Technician Dashboard', industry: 'pest_control' },
  // Moving (NEW v6.0)
  moving_owner: { name: 'Moving Company Dashboard', industry: 'moving' },
  moving_operations: { name: 'Operations Dashboard', industry: 'moving' },
};

// Sub-category specific dashboards (v4.0 - 45+ specialized views)
export const SUBCATEGORY_DASHBOARDS = {
  // Professional Services Sub-Categories
  lawyer_dashboard: { name: 'Lawyer Practice Dashboard', sub_category: 'lawyer' },
  accountant_dashboard: { name: 'Accounting Practice Dashboard', sub_category: 'accountant' },
  financial_advisor_dashboard: { name: 'Financial Advisor Dashboard', sub_category: 'financial_advisor' },
  consultant_dashboard: { name: 'Consulting Practice Dashboard', sub_category: 'consultant' },
  architect_dashboard: { name: 'Architecture Practice Dashboard', sub_category: 'architect' },
  engineer_dashboard: { name: 'Engineering Practice Dashboard', sub_category: 'engineer' },
  
  // Healthcare Sub-Categories
  gp_dashboard: { name: 'GP Practice Dashboard', sub_category: 'general_practice' },
  specialist_dashboard: { name: 'Specialist Clinic Dashboard', sub_category: 'specialist' },
  allied_health_dashboard: { name: 'Allied Health Dashboard', sub_category: 'allied_health' },
  dental_dashboard: { name: 'Dental Practice Dashboard', sub_category: 'dental' },
  aged_care_dashboard: { name: 'Aged Care Dashboard', sub_category: 'aged_care' },
  mental_health_dashboard: { name: 'Mental Health Practice Dashboard', sub_category: 'mental_health' },
  
  // Construction Sub-Categories
  builder_dashboard: { name: 'Builder Dashboard', sub_category: 'builder' },
  electrician_dashboard: { name: 'Electrical Contractor Dashboard', sub_category: 'electrician' },
  plumber_dashboard: { name: 'Plumbing Business Dashboard', sub_category: 'plumber' },
  hvac_dashboard: { name: 'HVAC Business Dashboard', sub_category: 'hvac' },
  landscaper_dashboard: { name: 'Landscaping Dashboard', sub_category: 'landscaper' },
  
  // Hospitality Sub-Categories
  restaurant_dashboard: { name: 'Restaurant Dashboard', sub_category: 'restaurant' },
  cafe_dashboard: { name: 'Cafe Dashboard', sub_category: 'cafe' },
  bar_dashboard: { name: 'Bar/Pub Dashboard', sub_category: 'bar' },
  hotel_dashboard: { name: 'Hotel Dashboard', sub_category: 'hotel' },
  gaming_venue_dashboard: { name: 'Gaming Venue Dashboard', sub_category: 'gaming_venue' },
  qsr_dashboard: { name: 'QSR/Fast Food Dashboard', sub_category: 'qsr' },
  catering_dashboard: { name: 'Catering Dashboard', sub_category: 'catering' },
  
  // Retail Sub-Categories
  fashion_retail_dashboard: { name: 'Fashion Retail Dashboard', sub_category: 'fashion' },
  electronics_dashboard: { name: 'Electronics Retail Dashboard', sub_category: 'electronics' },
  ecommerce_dashboard: { name: 'E-commerce Dashboard', sub_category: 'ecommerce' },
  specialty_retail_dashboard: { name: 'Specialty Retail Dashboard', sub_category: 'specialty' },
  
  // Fitness Sub-Categories
  gym_dashboard: { name: 'Gym Dashboard', sub_category: 'gym' },
  yoga_studio_dashboard: { name: 'Yoga/Pilates Studio Dashboard', sub_category: 'yoga_studio' },
  personal_training_dashboard: { name: 'PT Business Dashboard', sub_category: 'personal_training' },
  
  // Beauty Sub-Categories
  salon_dashboard: { name: 'Hair Salon Dashboard', sub_category: 'salon' },
  spa_dashboard: { name: 'Day Spa Dashboard', sub_category: 'spa' },
  cosmetic_clinic_dashboard: { name: 'Cosmetic Clinic Dashboard', sub_category: 'cosmetic_clinic' },
  barbershop_dashboard: { name: 'Barbershop Dashboard', sub_category: 'barbershop' },
  
  // Technology Sub-Categories
  saas_dashboard: { name: 'SaaS Company Dashboard', sub_category: 'saas' },
  it_services_dashboard: { name: 'IT Services Dashboard', sub_category: 'it_services' },
  digital_agency_dashboard: { name: 'Digital Agency Dashboard', sub_category: 'digital_agency' },
  
  // Finance Sub-Categories
  mortgage_broker_dashboard: { name: 'Mortgage Broker Dashboard', sub_category: 'mortgage_broker' },
  insurance_broker_dashboard: { name: 'Insurance Broker Dashboard', sub_category: 'insurance_broker' },
  financial_planner_dashboard: { name: 'Financial Planner Dashboard', sub_category: 'financial_planner' },
  
  // Real Estate Sub-Categories
  residential_sales_dashboard: { name: 'Residential Sales Dashboard', sub_category: 'residential_sales' },
  property_management_dashboard: { name: 'Property Management Dashboard', sub_category: 'property_management' },
  commercial_realestate_dashboard: { name: 'Commercial Real Estate Dashboard', sub_category: 'commercial' },
};

// ============================================
// CALCULATED STATISTICS (Auto-derived)
// ============================================

export const getDataSourcesCount = () => Object.keys(DATA_SOURCES).length;

// OLD: getIndustriesCount from SUPPORTED_INDUSTRIES (31 primary industries)
// This is kept for internal reference
export const getIndustriesCount = () => Object.keys(SUPPORTED_INDUSTRIES).length;
export const getRoleDashboardsCount = () => Object.keys(ROLE_DASHBOARDS).length;
export const getIndustryDashboardsCount = () => Object.keys(INDUSTRY_DASHBOARDS).length;
export const getSubcategoryDashboardsCount = () => Object.keys(SUBCATEGORY_DASHBOARDS).length;
export const getTotalDashboardsCount = () => 
  getRoleDashboardsCount() + getIndustryDashboardsCount() + getSubcategoryDashboardsCount();

// Sub-vertical counts (old system)
export const getSubVerticalsCount = () => {
  return Object.values(INDUSTRY_SUB_VERTICALS).reduce((total, arr) => total + arr.length, 0);
};

// Lightweight counts (kept out of the eager bundle — see platformCounts.js).
// The full industryConfig data module is only loaded by lazy components that
// render the actual industry lists.
import { TOTAL_INDUSTRY_COVERAGE, INDUSTRY_CATEGORIES_COUNT } from './platformCounts';

// Total industry coverage - comprehensive count (source: industryConfig)
export const getTotalIndustryCoverage = () => {
  return TOTAL_INDUSTRY_COVERAGE;
};

// Get total category count
export const getIndustryCategoriesCount = () => {
  return INDUSTRY_CATEGORIES_COUNT;
};

// Get industries with sub-verticals
export const getIndustriesWithSubVerticals = () => {
  return Object.entries(SUPPORTED_INDUSTRIES)
    .filter(([, value]) => value.hasSubVerticals)
    .map(([key]) => key);
};

// Integration count - imports from integrationsConfig
// This will be calculated dynamically when needed
let cachedIntegrationsCount = null;
export const getIntegrationsCount = () => {
  if (cachedIntegrationsCount !== null) return cachedIntegrationsCount;
  
  // Default to 162 (will be set by integrationsConfig.js on load)
  // This is updated dynamically when integrationsConfig is imported
  cachedIntegrationsCount = 167; // Safe default matching current integrations count
  return cachedIntegrationsCount;
};

// Set integrations count (called from integrationsConfig.js)
export const setIntegrationsCount = (count) => {
  cachedIntegrationsCount = count;
};

// ============================================
// PLATFORM STATS OBJECT (Use this everywhere)
// ============================================

export const getPlatformStats = () => ({
  dataSources: getDataSourcesCount(),
  dataSourcesDisplay: `${getDataSourcesCount()}+`,
  
  integrations: getIntegrationsCount(),
  integrationsDisplay: `${getIntegrationsCount()}+`,
  
  // Customer-facing industry number = total coverage from industryConfig (540+)
  industries: getTotalIndustryCoverage(),
  industriesDisplay: `${getTotalIndustryCoverage()}+`,
  
  // Industry categories (24)
  industryCategories: getIndustryCategoriesCount(),
  industryCategoriesDisplay: getIndustryCategoriesCount().toString(),
  
  // Internal counts (for reference - old system)
  _primaryIndustries: getIndustriesCount(),
  _subVerticals: getSubVerticalsCount(),
  
  // Sub-verticals (new)
  subVerticals: getSubVerticalsCount(),
  totalIndustryCoverage: getTotalIndustryCoverage(),
  totalIndustryCoverageDisplay: `${getTotalIndustryCoverage()}+`,
  
  // Dashboard counts
  roleDashboards: getRoleDashboardsCount(),
  industryDashboards: getIndustryDashboardsCount(),
  subcategoryDashboards: getSubcategoryDashboardsCount(),
  dashboards: getTotalDashboardsCount(),
  dashboardsDisplay: `${getTotalDashboardsCount()}+`,
  
  // Document version info - v6.0 with Legal AI, AEO Service, Advisor Intelligence
  documentVersion: '6.0',
  currentVersionDate: new Date().toISOString().split('T')[0], // Always use current date
  previousVersionDate: '2025-12-27',
});

// Export as constant for easy access
export const PLATFORM_STATS = getPlatformStats();

// Calculate days since a date
export const calculateDaysSince = (dateStr) => {
  const targetDate = new Date(dateStr);
  const today = new Date();
  const diffTime = Math.abs(today - targetDate);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Get update message
export const getUpdateMessage = () => {
  const days = calculateDaysSince(PLATFORM_STATS.currentVersionDate);
  if (days === 0) return 'Updated today';
  if (days === 1) return '1 day since last update';
  return `${days} days since last update`;
};

// ============================================
// HELPER FUNCTIONS
// ============================================

export const getDataSourcesText = (includeWord = true) => {
  const count = getDataSourcesCount();
  return includeWord ? `${count}+ data sources` : `${count}+`;
};

export const getIntegrationsText = (includeWord = true) => {
  const count = getIntegrationsCount();
  return includeWord ? `${count}+ integrations` : `${count}+`;
};

export const getIndustriesText = (includeWord = true) => {
  const count = getIndustriesCount();
  return includeWord ? `${count} industries` : count.toString();
};

export const getDashboardsText = (includeWord = true) => {
  const count = getTotalDashboardsCount();
  return includeWord ? `${count}+ specialized dashboards` : `${count}+`;
};

// Get data sources by category
export const getDataSourcesByCategory = () => {
  const byCategory = {};
  Object.entries(DATA_SOURCES).forEach(([key, source]) => {
    if (!byCategory[source.category]) {
      byCategory[source.category] = [];
    }
    byCategory[source.category].push({ key, ...source });
  });
  return byCategory;
};

// ============================================
// DEFAULT EXPORT
// ============================================

export default {
  DATA_SOURCES,
  SUPPORTED_INDUSTRIES,
  ROLE_DASHBOARDS,
  INDUSTRY_DASHBOARDS,
  SUBCATEGORY_DASHBOARDS,
  PLATFORM_STATS,
  getDataSourcesCount,
  getIntegrationsCount,
  getIndustriesCount,
  getRoleDashboardsCount,
  getIndustryDashboardsCount,
  getSubcategoryDashboardsCount,
  getTotalDashboardsCount,
  getPlatformStats,
  getDataSourcesText,
  getIntegrationsText,
  getIndustriesText,
  getDashboardsText,
  getDataSourcesByCategory,
  setIntegrationsCount,
  calculateDaysSince,
  getUpdateMessage,
};
